import { Suspense, useEffect } from 'react';
import { Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import theme from './theme/theme';
import * as Sentry from '@sentry/react';
import { useAuthStore } from './stores/authStore';
import ErrorBoundary from './components/common/ErrorBoundary';
import { buildRoutes } from './routes/RouterConfig';

// ─── Suspense Fallback ──────────────────────────────────────────────────────

function PageLoader() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <CircularProgress size={40} sx={{ color: '#6B3FA0' }} />
    </Box>
  );
}

// ─── React Query Client ─────────────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (error) => {
        Sentry.captureException(error, {
          tags: { source: 'react-query-mutation' },
        });
      },
    },
  },
});

// ─── App Component ──────────────────────────────────────────────────────────

export default function App() {
  const loadFromStorage = useAuthStore((s) => s.loadFromStorage);

  // Rehydrate user object from /auth/me on page load/refresh
  useEffect(() => {
    loadFromStorage();
  }, [loadFromStorage]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {buildRoutes()}
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
